import { FC, useEffect, useRef, useState } from "react";

export type InlineEditType = "text" | "date" | "currency" | "select";

export interface InlineSelectOption {
  value: string;
  label: string;
}

interface Props {
  /** Current raw value (string for text/date/select, number-as-string for currency). */
  value: string;
  /** Text shown when there is no value. */
  placeholder?: string;
  type?: InlineEditType;
  options?: InlineSelectOption[];
  /** Rendered display node when not editing (defaults to the value / placeholder). */
  display?: React.ReactNode;
  /** Called with the new raw value on save. May be async. */
  onSave: (next: string) => void | Promise<void>;
}

/**
 * Click-to-edit field. Shows a dashed-underline affordance on hover; clicking
 * swaps to an inline editor with save (✓) / cancel (✕) controls. Used across the
 * contract "one canvas" details grid.
 */
const InlineEditField: FC<Props> = ({
  value,
  placeholder = "— add",
  type = "text",
  options,
  display,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  const commit = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type !== "select") {
      e.preventDefault();
      void commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (!editing) {
    const hasValue = value !== "" && value != null;
    return (
      <span
        className={`inline-editable${hasValue ? "" : " is-empty"}`}
        role="button"
        tabIndex={0}
        title="Click to edit"
        onClick={start}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            start();
          }
        }}
      >
        {hasValue ? display ?? value : placeholder}
      </span>
    );
  }

  return (
    <span className="inline-editing">
      {type === "select" ? (
        <select
          ref={(el) => (inputRef.current = el)}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        >
          {options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          ref={(el) => (inputRef.current = el)}
          type={type === "date" ? "date" : type === "currency" ? "number" : "text"}
          step={type === "currency" ? "0.01" : undefined}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        />
      )}
      <span className="inline-editing__actions">
        <button type="button" className="ok" disabled={saving} title="Save" onClick={commit}>
          <i className="bi bi-check-lg" />
        </button>
        <button type="button" className="no" disabled={saving} title="Cancel" onClick={cancel}>
          <i className="bi bi-x-lg" />
        </button>
      </span>
    </span>
  );
};

export default InlineEditField;
